import { useState } from 'react'

import { PIN_MAX_LENGTH, PIN_MIN_LENGTH, type PinResult } from '@/platform/family/api'
import { toAppError } from '@/shared/errors/envelope'
import { PinPad } from '@/shared/ui/PinPad'

import { formatSeconds, useCountdown } from './countdown'

/**
 * Screen G, parent entry from the learner-selection screen (D-16): the
 * existing parent PIN (SEC-C, `verify_parent_pin`, 5 wrong → 5 minutes)
 * guards Parent Home exactly as leaving child mode does.
 */

export interface ParentGateScreenProps {
  readonly initialLockedUntil: string | null
  readonly onVerifyPin: (pin: string) => Promise<PinResult>
  readonly onUnlocked: () => void
  readonly onBack: () => void
  readonly now?: () => number
}

export function ParentGateScreen({
  initialLockedUntil,
  onVerifyPin,
  onUnlocked,
  onBack,
  now = Date.now,
}: ParentGateScreenProps): React.JSX.Element {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState<string | null>(initialLockedUntil)
  const lockSeconds = useCountdown(lockedUntil, now)

  const verify = async (pin: string) => {
    setBusy(true)
    setError(null)
    try {
      const result = await onVerifyPin(pin)
      if (result.verified) {
        onUnlocked()
        return
      }
      if (result.locked_until !== null) setLockedUntil(result.locked_until)
      else setError(`PIN chưa đúng. Còn ${String(result.attempts_remaining)} lần thử.`)
    } catch (e) {
      setError(
        toAppError(e).code === 'SESSION_EXPIRED'
          ? 'Phiên đăng nhập đã hết hạn.'
          : 'Không kiểm tra được PIN. Vui lòng thử lại.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col justify-center gap-5 px-6 pb-10">
      {lockSeconds > 0 ? (
        <div className="text-center" role="alert">
          <p className="text-4xl" aria-hidden>
            ⏳
          </p>
          <p className="mt-3 text-lg font-semibold">Đã nhập sai PIN 5 lần</p>
          <p className="mt-1 text-slate-700 dark:text-slate-300">
            Thử lại sau {formatSeconds(lockSeconds)}.
          </p>
          <button
            type="button"
            className="mt-5 px-4 text-slate-600 underline dark:text-slate-400"
            onClick={onBack}
          >
            Quay lại
          </button>
        </div>
      ) : (
        <PinPad
          title="Nhập PIN phụ huynh"
          error={error}
          minLength={PIN_MIN_LENGTH}
          maxLength={PIN_MAX_LENGTH}
          busy={busy}
          onSubmit={(pin) => void verify(pin)}
          onCancel={onBack}
        />
      )}
    </main>
  )
}
